import { isExamId } from "../data/exams";
import { isLang, type L, type Lang } from "../i18n/lang";
import type { ExamId } from "../data/types";
import { dayKey, daysBetween } from "./dates";
import { nextSrs, type SrsState } from "./srs";

export type Theme = "system" | "light" | "dark";
export type SessionKind = "practice" | "mock" | "daily" | "review" | "diagnostic";

export interface QuestionStat {
  seen: number;
  correct: number;
  wrong: number;
  lastAt: number;
  lastCorrect: boolean;
  srs: SrsState;
}

export interface DomainTally {
  correct: number;
  total: number;
}

export interface Attempt {
  id: string;
  kind: SessionKind;
  examId?: ExamId;
  title: string;
  label?: L;
  startedAt: number;
  finishedAt: number;
  questionIds: string[];
  answers: Record<string, number[]>;
  correct: number;
  total: number;
  scaled: number;
  passed: boolean;
  byDomain: Record<string, DomainTally>;
  timedOut?: boolean;
}

export interface Progress {
  version: 1;
  questions: Record<string, QuestionStat>;
  bookmarks: string[];
  attempts: Attempt[];
  activity: Record<string, { answered: number; correct: number }>;
  dailyDone: Record<string, { correct: number; total: number }>;
  streak: { current: number; best: number; lastDay?: string };
  xp: number;
  cards: Record<string, SrsState>;
  achievements: Record<string, number>;
  /** Lab thực hành: thời điểm hoàn thành & điểm tốt nhất (0–100). */
  labs: Record<string, { completedAt: number; best: number }>;
  /** Bài học đã hoàn thành: id → thời điểm. */
  lessons: Record<string, number>;
  /** Task statement đã tự đánh giá "nắm vững": "<exam>:<id>" → thời điểm. */
  taskChecks: Record<string, number>;
  /** Kế hoạch học đang theo. */
  plan?: StudyPlanConfig;
  theme: Theme;
  /** Ngôn ngữ giao diện & nội dung (đề thi luôn bằng tiếng Anh). */
  lang: Lang;
}

export interface StudyPlanConfig {
  exam: ExamId;
  weeks: 2 | 4 | 8;
  createdAt: number;
}

export const MAX_ATTEMPTS_KEPT = 100;
export const XP = { correct: 10, wrong: 2, card: 3, daily: 30, mockPass: 100, lesson: 20, lab: 50 } as const;

export function emptyProgress(): Progress {
  return {
    version: 1,
    questions: {},
    bookmarks: [],
    attempts: [],
    activity: {},
    dailyDone: {},
    streak: { current: 0, best: 0 },
    xp: 0,
    cards: {},
    achievements: {},
    labs: {},
    lessons: {},
    taskChecks: {},
    theme: "system",
    lang: "vi",
  };
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Đọc dữ liệu không tin cậy (localStorage / file import) thành Progress hợp lệ. */
export function parseProgress(raw: unknown): Progress {
  const base = emptyProgress();
  if (!isObj(raw) || raw.version !== 1) return base;
  const streak = isObj(raw.streak) ? raw.streak : {};
  return {
    version: 1,
    questions: isObj(raw.questions) ? (raw.questions as Progress["questions"]) : {},
    bookmarks: Array.isArray(raw.bookmarks) ? raw.bookmarks.filter((x): x is string => typeof x === "string") : [],
    attempts: Array.isArray(raw.attempts) ? (raw.attempts as Attempt[]).slice(0, MAX_ATTEMPTS_KEPT) : [],
    activity: isObj(raw.activity) ? (raw.activity as Progress["activity"]) : {},
    dailyDone: isObj(raw.dailyDone) ? (raw.dailyDone as Progress["dailyDone"]) : {},
    streak: {
      current: typeof streak.current === "number" ? streak.current : 0,
      best: typeof streak.best === "number" ? streak.best : 0,
      ...(typeof streak.lastDay === "string" ? { lastDay: streak.lastDay } : {}),
    },
    xp: typeof raw.xp === "number" && Number.isFinite(raw.xp) ? raw.xp : 0,
    cards: isObj(raw.cards) ? (raw.cards as Progress["cards"]) : {},
    achievements: isObj(raw.achievements) ? (raw.achievements as Progress["achievements"]) : {},
    labs: isObj(raw.labs) ? (raw.labs as Progress["labs"]) : {},
    lessons: isObj(raw.lessons) ? (raw.lessons as Progress["lessons"]) : {},
    taskChecks: isObj(raw.taskChecks) ? (raw.taskChecks as Progress["taskChecks"]) : {},
    ...(isObj(raw.plan) && isExamId(String(raw.plan.exam)) && [2, 4, 8].includes(Number(raw.plan.weeks))
      ? { plan: raw.plan as unknown as StudyPlanConfig }
      : {}),
    theme: raw.theme === "light" || raw.theme === "dark" ? raw.theme : "system",
    lang: isLang(raw.lang) ? raw.lang : "vi",
  };
}

/** Cập nhật streak khi có hoạt động học trong ngày `today`. */
export function touchStreak(s: Progress["streak"], today: string): Progress["streak"] {
  if (s.lastDay === today) return s;
  const gap = s.lastDay ? daysBetween(s.lastDay, today) : Infinity;
  const current = gap === 1 ? s.current + 1 : 1;
  return { current, best: Math.max(s.best, current), lastDay: today };
}

/** Streak hiển thị: về 0 nếu đã bỏ lỡ hơn một ngày. */
export function liveStreak(s: Progress["streak"], today: string): number {
  if (!s.lastDay) return 0;
  return daysBetween(s.lastDay, today) <= 1 ? s.current : 0;
}

export function recordAnswer(p: Progress, qid: string, correct: boolean, now: number): Progress {
  const today = dayKey(new Date(now));
  const prev = p.questions[qid];
  const stat: QuestionStat = {
    seen: (prev?.seen ?? 0) + 1,
    correct: (prev?.correct ?? 0) + (correct ? 1 : 0),
    wrong: (prev?.wrong ?? 0) + (correct ? 0 : 1),
    lastAt: now,
    lastCorrect: correct,
    srs: nextSrs(prev?.srs, correct, now),
  };
  const act = p.activity[today] ?? { answered: 0, correct: 0 };
  return {
    ...p,
    questions: { ...p.questions, [qid]: stat },
    activity: { ...p.activity, [today]: { answered: act.answered + 1, correct: act.correct + (correct ? 1 : 0) } },
    streak: touchStreak(p.streak, today),
    xp: p.xp + (correct ? XP.correct : XP.wrong),
  };
}

export function recordCard(p: Progress, cardId: string, knew: boolean, now: number): Progress {
  const today = dayKey(new Date(now));
  return {
    ...p,
    cards: { ...p.cards, [cardId]: nextSrs(p.cards[cardId], knew, now) },
    streak: touchStreak(p.streak, today),
    xp: p.xp + XP.card,
  };
}

export function addAttempt(p: Progress, a: Attempt): Progress {
  let xp = p.xp;
  let dailyDone = p.dailyDone;
  if (a.kind === "mock" && a.passed) xp += XP.mockPass;
  if (a.kind === "daily") {
    const day = dayKey(new Date(a.finishedAt));
    if (!dailyDone[day]) {
      xp += XP.daily;
      dailyDone = { ...dailyDone, [day]: { correct: a.correct, total: a.total } };
    }
  }
  return { ...p, xp, dailyDone, attempts: [a, ...p.attempts].slice(0, MAX_ATTEMPTS_KEPT) };
}

export function toggleBookmark(p: Progress, qid: string): Progress {
  const has = p.bookmarks.includes(qid);
  return { ...p, bookmarks: has ? p.bookmarks.filter((x) => x !== qid) : [...p.bookmarks, qid] };
}

/** Cấp độ từ XP: mỗi cấp cần nhiều XP hơn cấp trước (250, 500, 750…). */
export function levelFromXp(xp: number): { level: number; into: number; need: number } {
  let level = 1;
  let remaining = xp;
  let need = 250;
  while (remaining >= need) {
    remaining -= need;
    level += 1;
    need = 250 * level;
  }
  return { level, into: remaining, need };
}

export function completeLesson(p: Progress, lessonId: string, now: number): Progress {
  if (p.lessons[lessonId]) return p;
  return {
    ...p,
    lessons: { ...p.lessons, [lessonId]: now },
    streak: touchStreak(p.streak, dayKey(new Date(now))),
    xp: p.xp + XP.lesson,
  };
}

/** Ghi kết quả lab; XP chỉ cộng lần đầu đạt 100 điểm. */
export function recordLab(p: Progress, labId: string, score: number, now: number): Progress {
  const prev = p.labs[labId];
  const best = Math.max(prev?.best ?? 0, score);
  const firstPerfect = score === 100 && (prev?.best ?? 0) < 100;
  return {
    ...p,
    labs: { ...p.labs, [labId]: { completedAt: prev?.completedAt ?? now, best } },
    streak: touchStreak(p.streak, dayKey(new Date(now))),
    xp: p.xp + (firstPerfect ? XP.lab : 0),
  };
}

export function toggleTaskCheck(p: Progress, key: string, now: number): Progress {
  const next = { ...p.taskChecks };
  if (next[key]) delete next[key];
  else next[key] = now;
  return { ...p, taskChecks: next };
}
